import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { MaterialReactTable, useMaterialReactTable } from "material-react-table";
import { MaterialReactTableConfig } from "components/common/table/TableConfig";
import { usePageTitle } from "context/PageTitleContext";
import { PERMISSIONS } from "auth/permissionCodes";
import { usePermissions } from "hooks/usePermissions";
import {
  createDocumentType,
  deleteDocumentType,
  getDocumentTypes,
  updateDocumentType,
} from "../../services/documentType.service";
import AddDocumentTypeDialog from "./AddDocumentTypeDialog";
import DeleteDocumentTypeDialog from "./DeleteDocumentTypeDialog";
import DataTable from "components/common/tables/DataTable";
import { AppButton } from "components/common/buttons";
import { AppSelect } from "components/common/inputs";
import { AppFilterBar } from "components/common/filters";

const ALL_DOCUMENT_CATEGORIES = "__ALL_DOCUMENT_CATEGORIES__";

const normalizeDocumentType = (item) => ({
  Id: item?.Id ?? item?.id,
  Name: item?.Name ?? item?.name ?? "",
  DocumentCategory: item?.DocumentCategory ?? item?.documentCategory ?? "",
  Note: item?.Note ?? item?.note ?? "",
});

const getResponseMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.Message ||
  error?.message ||
  fallback;

const DocumentTypeManagement = () => {
  //#region State & variables
  const { setPageTitle } = usePageTitle();
  const permissions = usePermissions();
  const canCreate = permissions.can(PERMISSIONS.SYSTEM.DOCUMENT_TYPE_CREATE);
  const canUpdate = permissions.can(PERMISSIONS.SYSTEM.DOCUMENT_TYPE_UPDATE);
  const canDelete = permissions.can(PERMISSIONS.SYSTEM.DOCUMENT_TYPE_DELETE);
  const canMutate = canUpdate || canDelete;
  const [data, setData] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(ALL_DOCUMENT_CATEGORIES);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  //#endregion

  //#region Effects & handlers
  useEffect(() => {
    setPageTitle("Quản lý danh mục tài liệu");
  }, [setPageTitle]);

  const showSnackbar = useCallback((message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const loadCategoryOptions = useCallback(async () => {
    const response = await getDocumentTypes({
      pageSize: 1000,
      pageNumber: 1,
      sortColumn: "DocumentCategory",
      sortDirection: "ASC",
    });

    const rows = Array.isArray(response?.data)
      ? response.data
      : Array.isArray(response?.Data)
        ? response.Data
        : [];

    const options = Array.from(
      new Set(
        rows
          .map((item) => normalizeDocumentType(item).DocumentCategory)
          .filter(Boolean)
      )
    ).sort((a, b) => a.localeCompare(b, "vi"));

    setCategoryOptions(options);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getDocumentTypes({
        documentCategory:
          selectedCategory === ALL_DOCUMENT_CATEGORIES ? undefined : selectedCategory,
        pageSize: 1000,
        pageNumber: 1,
        sortColumn: "DocumentCategory",
        sortDirection: "ASC",
      });

      const rows = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.Data)
          ? response.Data
          : [];

      setData(rows.map(normalizeDocumentType));
    } catch (error) {
      setData([]);
      showSnackbar(getResponseMessage(error, "Lỗi khi tải danh sách loại tài liệu"), "error");
    } finally {
      setLoading(false);
    }
  }, [selectedCategory, showSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    loadCategoryOptions().catch((error) => {
      showSnackbar(getResponseMessage(error, "Lỗi khi tải nhóm tài liệu"), "error");
    });
  }, [loadCategoryOptions, showSnackbar]);

  const reloadAfterMutation = useCallback(async () => {
    await Promise.all([loadData(), loadCategoryOptions()]);
  }, [loadCategoryOptions, loadData]);

  const handleCreate = async (payload) => {
    setSaving(true);
    try {
      await createDocumentType(payload);
      setAddOpen(false);
      showSnackbar("Thêm loại tài liệu thành công", "success");
      await reloadAfterMutation();
    } catch (error) {
      showSnackbar(getResponseMessage(error, "Lỗi khi thêm loại tài liệu"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRow = async ({ values, row, exitEditingMode }) => {
    const nextErrors = {};
    const name = String(values.Name || "").trim();
    const documentCategory = String(values.DocumentCategory || "").trim();

    if (!name) nextErrors.Name = "Vui lòng nhập tên loại tài liệu";
    if (!documentCategory) {
      nextErrors.DocumentCategory = "Vui lòng chọn nhóm tài liệu";
    }

    setValidationErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSaving(true);
    try {
      await updateDocumentType(row.original.Id, {
        Name: name,
        DocumentCategory: documentCategory,
        Note: String(values.Note || "").trim() || null,
      });
      exitEditingMode();
      showSnackbar("Cập nhật loại tài liệu thành công", "success");
      await reloadAfterMutation();
    } catch (error) {
      showSnackbar(getResponseMessage(error, "Lỗi khi cập nhật loại tài liệu"), "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.Id) return;

    setSaving(true);
    try {
      await deleteDocumentType(deleteTarget.Id);
      setDeleteTarget(null);
      showSnackbar("Xóa loại tài liệu thành công", "success");
      await reloadAfterMutation();
    } catch (error) {
      showSnackbar(getResponseMessage(error, "Lỗi khi xóa loại tài liệu"), "error");
    } finally {
      setSaving(false);
    }
  };

  const documentCategoryEditOptions = useMemo(() => {
    const options = new Set(categoryOptions);
    data.forEach((item) => {
      if (item.DocumentCategory) options.add(item.DocumentCategory);
    });
    return Array.from(options).sort((a, b) => a.localeCompare(b, "vi"));
  }, [categoryOptions, data]);
  //#endregion

  //#region Column setup
  const columns = useMemo(
    () => [
      {
        accessorKey: "Id",
        header: "Mã loại tài liệu",
        enableEditing: false,
        size: 70,
      },
      {
        accessorKey: "Name",
        header: "Tên loại tài liệu",
        size: 260,
        muiEditTextFieldProps: {
          required: true,
          error: Boolean(validationErrors.Name),
          helperText: validationErrors.Name,
          size: "small",
        },
      },
      {
        accessorKey: "DocumentCategory",
        header: "Nhóm tài liệu",
        size: 220,
        editVariant: "select",
        editSelectOptions: documentCategoryEditOptions,
        muiEditTextFieldProps: {
          required: true,
          error: Boolean(validationErrors.DocumentCategory),
          helperText: validationErrors.DocumentCategory,
          size: "small",
        },
      },
      {
        accessorKey: "Note",
        header: "Ghi chú",
        size: 360,
        Cell: ({ cell }) => cell.getValue() || "-",
        muiEditTextFieldProps: {
          multiline: true,
          minRows: 2,
          size: "small",
        },
      },
    ],
    [documentCategoryEditOptions, validationErrors]
  );
  //#endregion

  //#region Table setup
  const baseTableConfig = useMemo(() => MaterialReactTableConfig(), []);

  const table = useMaterialReactTable({
    ...baseTableConfig,
    columns,
    data,
    getRowId: (row) => String(row.Id),
    enableEditing: canUpdate,
    editDisplayMode: "row",
    // enableGlobalFilter: false,
    // enableColumnFilters: false,
    // enableFilters: false,
    // enableHiding: false,
    // enableTopToolbar: false,
    // enableColumnActions: false,
    // enableDensityToggle: false,
    // enableFullScreenToggle: false,
    enableRowActions: canMutate,
    positionActionsColumn: "last",
    onEditingRowSave: handleSaveRow,
    onEditingRowCancel: () => setValidationErrors({}),
    displayColumnDefOptions: {
      "mrt-row-actions": {
        header: "Hành động",
        size: 100,
      },
    },
    renderRowActions: ({ row, table }) => (
      <Stack direction="row" spacing={0.5} justifyContent="center">
        <Tooltip title="Sửa">
          <IconButton
            size="small"
            color="primary"
            onClick={() => {
              setValidationErrors({});
              table.setEditingRow(row);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Xóa">
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteTarget(row.original)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    ),
    renderEmptyRowsFallback: () => (
      <Typography sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
        Không có dữ liệu
      </Typography>
    ),
    state: {
      isLoading: loading,
      showProgressBars: saving,
    },
    initialState: {
      ...baseTableConfig.initialState,
      density: "compact",
      pagination: { pageIndex: 0, pageSize: 10 },
      columnVisibility: {
        ...baseTableConfig.initialState?.columnVisibility,
        Id: false,
        "mrt-row-actions": true,
      },
    },
    muiTablePaperProps: {
      ...baseTableConfig.muiTablePaperProps,
      sx: {
        ...baseTableConfig.muiTablePaperProps?.sx,
        height: "100%",
      },
    },
    muiTableContainerProps: {
      ...baseTableConfig.muiTableContainerProps,
      sx: {
        ...baseTableConfig.muiTableContainerProps?.sx,
        maxHeight: "calc(100vh - 250px)",
        minHeight: "360px",
      },
    },
    muiTableBodyRowProps: {
      hover: true,
    },
  });
  //#endregion

  return (
    <Box sx={{ p: 2, height: "calc(100vh - 64px)", backgroundColor: "#f9fafb" }}>
      <Stack spacing={2} sx={{ height: "100%" }}>

        {/* <Paper
          elevation={0}
          sx={{
            p: 2,
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            backgroundColor: "#fff",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 280 } }}>
              <InputLabel>Nhóm tài liệu</InputLabel>
              <Select
                value={selectedCategory}
                label="Nhóm tài liệu"
                onChange={(event) => setSelectedCategory(event.target.value)}
              >
                <MenuItem value="">Tất cả nhóm tài liệu</MenuItem>
                {categoryOptions.map((category) => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setAddOpen(true)}
                disableElevation
                sx={{ whiteSpace: "nowrap" }}
              >
                Thêm mới
              </Button>
            )}
          </Stack>
        </Paper> */}

        <AppFilterBar icon={false}>
          <AppSelect
            label="Nhóm tài liệu"
            value={selectedCategory}
            options={[
              { label: "Tất cả nhóm tài liệu", value: ALL_DOCUMENT_CATEGORIES },
              ...categoryOptions.map(x => ({
                label: x,
                value: x,
              })),
            ]}
            onChange={(value) => setSelectedCategory(value)}
            sx={{ width: 280 }}
          />

          <Box sx={{ flex: 1 }} />

          {canCreate && (
            <AppButton
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setAddOpen(true)}
              disableElevation
              sx={{ whiteSpace: "nowrap" }}
            >
              Thêm mới
            </AppButton>
          )}
        </AppFilterBar>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          {/* <MaterialReactTable table={table} /> */}
          <DataTable
            columns={columns}
            data={data}
            enableEditing={canUpdate}
            enableRowActions={canMutate}
            onEditingRowSave={handleSaveRow}
            initialState={{
              columnVisibility: {
                Id: false,
              },
            }}
            renderRowActions={({ row, table }) => (
              <Stack direction="row" spacing={0.5} justifyContent="center">
                <Tooltip title="Sửa">
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      setValidationErrors({});
                      table.setEditingRow(row);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Xóa">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => setDeleteTarget(row.original)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            )}
          />
        </Box>
      </Stack>

      <AddDocumentTypeDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleCreate}
        documentCategoryOptions={categoryOptions}
        defaultDocumentCategory={
          selectedCategory === ALL_DOCUMENT_CATEGORIES ? "" : selectedCategory
        }
        submitting={saving}
      />

      <DeleteDocumentTypeDialog
        open={Boolean(deleteTarget)}
        documentType={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        submitting={saving}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentTypeManagement;
